import React, { useMemo, useState, useRef } from "react";
import {
  useCommunities,
  useGlobalUsers,
  useGlobalProperties,
  useDashboardStats,
} from "../hooks/useSuperAdmin";
import { Icon } from "@iconify/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useMe } from "../hooks/useMe";
import Logo from "../components/assets/logo.png";
import { Loader } from "lucide-react";
import {
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Checkbox,
} from "antd";
import { PlusOutlined, EyeOutlined, SearchOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createCommunity, createUser } from "../api/superAdminApi";
import toast from "react-hot-toast";
import SubdivisionManagement from "./subdivisionManagement";
import { useCommunityProperties } from "../hooks/useSuperAdmin";

// Searchable Filter Dropdown Component
const SearchableFilterDropdown = ({
  options,
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}) => {
  const [searchText, setSearchText] = useState("");
  const searchInput = useRef(null);

  const filteredOptions = options.filter((option) =>
    String(option.text || option.value)
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );

  const handleReset = () => {
    setSearchText("");
    setSelectedKeys([]);
    clearFilters();
  };

  const handleConfirm = () => {
    confirm();
  };

  return (
    <div style={{ padding: 8 }}>
      <Input
        ref={searchInput}
        placeholder="Search..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onPressEnter={handleConfirm}
        style={{ marginBottom: 8, display: "block" }}
        prefix={<SearchOutlined />}
      />
      <div style={{ maxHeight: 200, overflowY: "auto" }}>
        <Checkbox.Group
          value={selectedKeys}
          onChange={(values) => {
            setSelectedKeys(values);
          }}
          style={{ display: "flex", flexDirection: "column", width: "100%" }}
        >
          {filteredOptions.map((option) => (
            <Checkbox key={option.value} value={option.value}>
              {option.text}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </div>
      <Space
        style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}
      >
        <Button
          type="link"
          onClick={handleReset}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
        <Button
          type="primary"
          onClick={handleConfirm}
          size="small"
          style={{ width: 90 }}
        >
          OK
        </Button>
      </Space>
    </div>
  );
};

const SUPER_ADMIN_COMMUNITY_KEY = "superAdminCommunityId";

const SuperAdminDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { stats, isLoading: statsLoading } = useDashboardStats();
  const { communities, isLoading: communitiesLoading } = useCommunities();
  const { users, isLoading: usersLoading } = useGlobalUsers();
  const { properties, isLoading: propertiesLoading } = useGlobalProperties();

  const [activeTab, setActiveTab] = useState("overview");
  const [selectedCommunityIdState, setSelectedCommunityIdState] =
    useState(null);
  const selectedCommunityId = selectedCommunityIdState;
  const { properties: communityProperties } = useCommunityProperties(
    selectedCommunityId ?? undefined
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const setSelectedCommunityId = (id) => {
    const numId = id != null ? Number(id) : null;
    setSelectedCommunityIdState(numId);
    if (numId != null) {
      setSearchParams({ community: numId });
      sessionStorage.setItem(SUPER_ADMIN_COMMUNITY_KEY, String(numId));
      localStorage.setItem("communityId", String(numId));
    } else {
      setSearchParams({});
      sessionStorage.removeItem(SUPER_ADMIN_COMMUNITY_KEY);
      localStorage.removeItem("communityId");
    }
  };

  React.useEffect(() => {
    const fromUrl = searchParams.get("community");
    const fromStorage = sessionStorage.getItem(SUPER_ADMIN_COMMUNITY_KEY);
    const raw = fromUrl || fromStorage;
    if (raw) {
      const id = parseInt(raw, 10);
      if (Number.isFinite(id)) setSelectedCommunityIdState(id);
      if (id && !fromUrl) setSearchParams({ community: id });
      if (id) localStorage.setItem("communityId", String(id));
    }
  }, []);
  const [communityModalOpen, setCommunityModalOpen] = useState(false);
  const [rcrModalOpen, setRcrModalOpen] = useState(false);
  const [propertyModalOpen, setPropertyModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [communityForm] = Form.useForm();
  const [rcrForm] = Form.useForm();

  const queryClient = useQueryClient();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { me } = useMe();

  const handleLogOut = async () => {
    logout();
    navigate("/");
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const goToProjectPanel = (communityId) => {
    localStorage.setItem("communityId", String(communityId));
    navigate("/admin");
  };

  const goToAgentPanel = (communityId) => {
    localStorage.setItem("communityId", String(communityId));
    navigate("/agent-admin");
  };

  const menuItems = [
    {
      id: "overview",
      name: "Overview",
      icon: "material-symbols:dashboard-rounded",
    },
    { id: "community", name: "Community", icon: "mdi:city" },
    { id: "home-type", name: "Home type", icon: "mdi:home" },
    { id: "subdivisions", name: "Subdivisions", icon: "mdi:map-marker-radius" },
    { id: "communities", name: "Communities", icon: "mdi:city" },
    { id: "users", name: "Users", icon: "mdi:account-group" },
    { id: "properties", name: "Properties", icon: "mdi:home" },
  ];

  const normalizeHomeTypeForCount = (ht) => {
    if (!ht) return null;
    const t = String(ht).toUpperCase().replace(/\s/g, "");
    if (t === "SFH") return "SFH";
    if (t === "CONDO") return "CONDO";
    if (t === "TH") return "TH";
    if (t === "VILLA" || t === "VIL") return "VIL";
    if (t === "COACHHOME" || t === "COACH") return "Coach";
    return null;
  };

  const communitiesTableData = useMemo(() => {
    const countsByCommunity = {};
    (properties || []).forEach((p) => {
      const cid = p.community_id ?? p.community?.id;
      if (cid == null) return;
      if (!countsByCommunity[cid]) {
        countsByCommunity[cid] = { SFH: 0, CONDO: 0, TH: 0, VIL: 0 };
      }
      const key = normalizeHomeTypeForCount(p.home_type);
      if (key && countsByCommunity[cid].hasOwnProperty(key)) {
        countsByCommunity[cid][key]++;
      }
    });
    return (communities || []).map((c) => ({
      ...c,
      shortName: c.shortName ?? c.short_name ?? c.name,
      SFH: countsByCommunity[c.id]?.SFH ?? 0,
      CONDO: countsByCommunity[c.id]?.CONDO ?? 0,
      TH: countsByCommunity[c.id]?.TH ?? 0,
      VIL: countsByCommunity[c.id]?.VIL ?? 0,
    }));
  }, [communities, properties]);

  const homeTypeCountsForCommunity = useMemo(() => {
    const counts = { SFH: 0, CONDO: 0, TH: 0, VIL: 0, Coach: 0 };
    (communityProperties || []).forEach((p) => {
      const key = normalizeHomeTypeForCount(p.home_type);
      if (key && counts.hasOwnProperty(key)) counts[key]++;
    });
    return counts;
  }, [communityProperties]);

  const createCommunityMutation = useMutation({
    mutationFn: createCommunity,
    onSuccess: (data) => {
      toast.success(data?.message || "Community created");
      queryClient.invalidateQueries(["communities"]);
      communityForm.resetFields();
      setCommunityModalOpen(false);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create community"
      );
    },
  });

  const [selectedRole, setSelectedRole] = useState("Admin");
  const [managedCommunityIds, setManagedCommunityIds] = useState([]);

  const createUserMutation = useMutation({
    mutationFn: createUser,
    onSuccess: (data) => {
      toast.success(data?.message || "User created");
      queryClient.invalidateQueries(["globalUsers"]);
      rcrForm.resetFields();
      setSelectedRole("Admin");
      setManagedCommunityIds([]);
      setRcrModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create user");
    },
  });

  // Memoized data arrays (must be before early return)
  const propertiesData = useMemo(() => properties || [], [properties]);
  const communitiesData = useMemo(() => communities || [], [communities]);
  const usersData = useMemo(() => users || [], [users]);

  // Helper function to create searchable filter dropdown config
  const createSearchableFilter = (options, getValue) => {
    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <SearchableFilterDropdown
          options={options}
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      onFilter: (value, record) => {
        if (!value || value.length === 0) return true;
        const recordValue = getValue
          ? getValue(record)
          : record[options[0]?.dataIndex || ""];
        return value.includes(recordValue);
      },
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
    };
  };

  if (statsLoading || communitiesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  // Prepare filter options
  const propertyNumberOptions = Array.from(
    new Set((properties || []).map((p) => p.mls_number).filter(Boolean))
  ).map((v) => ({ text: v, value: v }));

  const homeTypeOptions = Array.from(
    new Set((properties || []).map((p) => p.home_type).filter(Boolean))
  ).map((v) => ({ text: v, value: v }));

  const statusOptions = [
    "Active",
    "Sold",
    "Rented",
    "Pending",
    "Incomplete",
  ].map((v) => ({ text: v, value: v }));

  const listingStatusOptions = Array.from(
    new Set((properties || []).map((p) => p.cw_listing_status).filter(Boolean))
  ).map((v) => ({ text: v, value: v }));

  const bedroomsOptions = Array.from(
    new Set(
      (properties || [])
        .map((p) => p.bedrooms)
        .filter((v) => v !== null && v !== undefined)
    )
  )
    .sort((a, b) => a - b)
    .map((v) => ({ text: String(v), value: v }));

  const bathroomsOptions = Array.from(
    new Set(
      (properties || [])
        .map((p) => p.bathrooms)
        .filter((v) => v !== null && v !== undefined)
    )
  )
    .sort((a, b) => a - b)
    .map((v) => ({ text: String(v), value: v }));

  const priceOptions = [
    { text: "Under $100k", value: "under100k" },
    { text: "$100k - $250k", value: "100k-250k" },
    { text: "$250k - $500k", value: "250k-500k" },
    { text: "$500k - $1M", value: "500k-1m" },
    { text: "Over $1M", value: "over1m" },
  ];

  const communityOptions = Array.from(
    new Set((properties || []).map((p) => p.community?.name).filter(Boolean))
  ).map((v) => ({ text: v, value: v }));

  const propertyColumns = [
    {
      title: "Property #",
      dataIndex: "mls_number",
      key: "mls_number",
      width: 120,
      sorter: (a, b) =>
        (a.mls_number || "").localeCompare(b.mls_number || ""),
      ...createSearchableFilter(
        propertyNumberOptions,
        (record) => record.mls_number
      ),
      render: (text) => text || "-",
    },
    {
      title: "Home Type",
      dataIndex: "home_type",
      key: "home_type",
      width: 100,
      ...createSearchableFilter(homeTypeOptions, (record) => record.home_type),
      sorter: (a, b) => (a.home_type || "").localeCompare(b.home_type || ""),
      render: (text) => text || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      ...createSearchableFilter(statusOptions, (record) => record.status),
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
      render: (status) => (
        <Tag
          color={
            (status || "").toLowerCase() === "sold"
              ? "green"
              : (status || "").toLowerCase() === "active"
              ? "blue"
              : (status || "").toLowerCase() === "rented"
              ? "orange"
              : "default"
          }
        >
          {status || "Unknown"}
        </Tag>
      ),
    },
    {
      title: "Listing Status",
      dataIndex: "cw_listing_status",
      key: "cw_listing_status",
      width: 120,
      ...createSearchableFilter(
        listingStatusOptions,
        (record) => record.cw_listing_status
      ),
      sorter: (a, b) =>
        (a.cw_listing_status || "").localeCompare(b.cw_listing_status || ""),
      render: (text) => text || "-",
    },
    {
      title: "Bedrooms",
      dataIndex: "bedrooms",
      key: "bedrooms",
      width: 80,
      sorter: (a, b) => (a.bedrooms || 0) - (b.bedrooms || 0),
      ...createSearchableFilter(bedroomsOptions, (record) => record.bedrooms),
      render: (text) => text || "-",
    },
    {
      title: "Bathrooms",
      dataIndex: "bathrooms",
      key: "bathrooms",
      width: 80,
      sorter: (a, b) => (a.bathrooms || 0) - (b.bathrooms || 0),
      ...createSearchableFilter(bathroomsOptions, (record) => record.bathrooms),
      render: (text) => text || "-",
    },
    {
      title: "Price/Rent",
      dataIndex: "price_or_rent",
      key: "price_or_rent",
      width: 120,
      sorter: (a, b) =>
        (Number(a.price_or_rent) || 0) - (Number(b.price_or_rent) || 0),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <SearchableFilterDropdown
          options={priceOptions}
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      onFilter: (value, record) => {
        if (!value || value.length === 0) return true;
        const price = Number(record.price_or_rent) || 0;
        return value.some((val) => {
          switch (val) {
            case "under100k":
              return price < 100000;
            case "100k-250k":
              return price >= 100000 && price < 250000;
            case "250k-500k":
              return price >= 250000 && price < 500000;
            case "500k-1m":
              return price >= 500000 && price < 1000000;
            case "over1m":
              return price >= 1000000;
            default:
              return false;
          }
        });
      },
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      render: (val) => (val ? `$${Number(val).toLocaleString()}` : "-"),
    },
    {
      title: "Community",
      dataIndex: ["community", "name"],
      key: "community_name",
      width: 150,
      sorter: (a, b) =>
        (a.community?.name || "").localeCompare(b.community?.name || ""),
      ...createSearchableFilter(
        communityOptions,
        (record) => record.community?.name
      ),
      render: (text) => text || "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedProperty(record);
            setPropertyModalOpen(true);
          }}
          size="small"
        >
          View
        </Button>
      ),
    },
  ];

  const communityColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Short Name", dataIndex: "shortName", key: "shortName" },
    { title: "Homes", dataIndex: "totalHomes", key: "totalHomes" },
    {
      title: "Established",
      dataIndex: "establishedYear",
      key: "establishedYear",
    },
  ];

  const userColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      filters: Array.from(new Set((users || []).map((u) => u.role))).map(
        (r) => ({ text: r, value: r })
      ),
      onFilter: (value, record) => record.role === value,
      render: (role) => <Tag>{role}</Tag>,
    },
    { title: "Community", dataIndex: "community_id", key: "community_id" },
  ];

  return (
    <div className="flex flex-col h-screen font-Poppins lg:flex-row">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 h-full bg-red-600 text-white shadow-md w-52
          transform ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
          transition-transform duration-300 z-30
          lg:static lg:translate-x-0 lg:w-52
          flex flex-col
        `}
      >
        <div className="flex items-center justify-between py-4 px-3 flex-shrink-0">
          <div className="flex items-center">
            <img src={Logo} alt="Logo" className="w-10 h-10 object-contain" />
            <span className="text-sm font-semibold hidden lg:inline-block ml-2">
              Super Admin
            </span>
          </div>
          <button
            type="button"
            className="lg:hidden text-white focus:outline-none"
            onClick={toggleSidebar}
            aria-label="Close Sidebar"
          >
            <Icon icon="mdi:close" className="text-xl" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 px-2">
          <button
            type="button"
            onClick={() => {
              setSelectedCommunityId(null);
              setActiveTab("overview");
              setIsSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition ${
              !selectedCommunityId
                ? "bg-red-500 text-white"
                : "hover:bg-red-700 hover:text-white"
            }`}
          >
            <Icon icon="mdi:home" className="text-lg" />
            <span className="ml-1">Home</span>
          </button>
          {!selectedCommunityId && (
            <p className="px-3 py-2 text-xs text-red-100 italic">
              No other tabs appear here until a community is selected in the
              table.
            </p>
          )}
          {selectedCommunityId != null &&
            menuItems
              .filter((item) => item.id !== "overview")
              .map(({ id, name, icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    setActiveTab(id);
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition ${
                    activeTab === id
                      ? "bg-red-500 text-white"
                      : "hover:bg-red-700 hover:text-white"
                  }`}
                  aria-current={activeTab === id ? "page" : undefined}
                >
                  <Icon icon={icon} className="text-lg" />
                  <span className="ml-1">{name}</span>
                </button>
              ))}
        </nav>

        <div className="flex-shrink-0 px-2 pb-3">
          <button
            type="button"
            onClick={handleLogOut}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md font-medium text-white hover:bg-red-700 transition"
          >
            <Icon icon="mdi:logout" className="text-lg" />
            <span className="ml-1">Logout</span>
          </button>
        </div>
      </aside>

      <header className="flex items-center justify-between bg-red-600 text-white p-4 lg:hidden">
        <button
          type="button"
          onClick={toggleSidebar}
          className="focus:outline-none"
          aria-label="Open Sidebar"
        >
          <Icon icon="mdi:menu" className="text-2xl" />
        </button>
        <img src={Logo} className="w-8 h-8 object-contain" alt="Logo" />
        <span className="text-lg font-semibold">Super Admin</span>
        <div className="w-8 h-8" />
      </header>

      <main className="flex-1 bg-gray-100 h-screen overflow-y-auto">
        <div className="max-w-7xl mx-auto p-4 lg:p-8">
          <div className="mb-4">
            <p className="text-lg font-semibold text-gray-900">
              {me?.name ?? "Super Admin"}
            </p>
            {selectedCommunityId != null && (
              <p className="text-xl font-bold text-red-700 uppercase mt-1">
                {communitiesData.find((c) => c.id === selectedCommunityId)
                  ?.shortName ??
                  communitiesData.find((c) => c.id === selectedCommunityId)
                    ?.name ??
                  "Community"}
              </p>
            )}
          </div>

          {!selectedCommunityId ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-4 uppercase">
                HOME
              </h1>
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <Table
                  dataSource={communitiesTableData}
                  rowKey="id"
                  pagination={false}
                  columns={[
                    {
                      title: "COM SHORT NAME",
                      dataIndex: "shortName",
                      key: "shortName",
                      render: (text, record) => (
                        <button
                          type="button"
                          onClick={() => setSelectedCommunityId(record.id)}
                          className="text-red-600 font-semibold hover:underline uppercase"
                        >
                          {text || record.name}
                        </button>
                      ),
                    },
                    {
                      title: () => (
                        <span>
                          PANEL
                          <div className="text-xs font-normal opacity-90">
                            Project
                          </div>
                        </span>
                      ),
                      key: "panelProject",
                      render: (_, record) => (
                        <button
                          type="button"
                          onClick={() => goToProjectPanel(record.id)}
                          className="text-blue-600 hover:underline"
                        >
                          PROJECT
                        </button>
                      ),
                    },
                    {
                      title: () => (
                        <span>
                          PANEL
                          <div className="text-xs font-normal opacity-90">
                            Agent
                          </div>
                        </span>
                      ),
                      key: "panelAgent",
                      render: (_, record) => (
                        <button
                          type="button"
                          onClick={() => goToAgentPanel(record.id)}
                          className="text-blue-600 hover:underline"
                        >
                          AGENT
                        </button>
                      ),
                    },
                    { title: "SFH", dataIndex: "SFH", key: "SFH", width: 72 },
                    {
                      title: "CONDO",
                      dataIndex: "CONDO",
                      key: "CONDO",
                      width: 80,
                    },
                    { title: "TH", dataIndex: "TH", key: "TH", width: 72 },
                    { title: "VIL", dataIndex: "VIL", key: "VIL", width: 72 },
                  ]}
                />
              </div>
            </>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                  Super Admin Dashboard
                </h1>
                <p className="text-gray-600 mt-1">
                  Manage community, users, and properties
                </p>
              </div>

              {/* Overview / Community Tab */}
              {(activeTab === "overview" || activeTab === "community") && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {/* Total Communities */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-sm font-medium text-gray-600">
                      Total Communities
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mt-2">
                      {stats?.totalCommunities || 0}
                    </div>
                  </div>

                  {/* Total Users */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-sm font-medium text-gray-600">
                      Total Users
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mt-2">
                      {stats?.totalUsers || 0}
                    </div>
                  </div>

                  {/* Total Properties */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-sm font-medium text-gray-600">
                      Total Properties
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mt-2">
                      {stats?.totalProperties || 0}
                    </div>
                  </div>

                  {/* Total Events */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-sm font-medium text-gray-600">
                      Total Events
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mt-2">
                      {stats?.totalEvents || 0}
                    </div>
                  </div>

                  {/* Total Blogs */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-sm font-medium text-gray-600">
                      Total Blogs
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mt-2">
                      {stats?.totalBlogs || 0}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "home-type" && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Property counts by home type
                  </h2>
                  {propertiesLoading ? (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Loader className="w-5 h-5 animate-spin" />
                      Loading…
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                      {[
                        { key: "SFH", label: "Single Family" },
                        { key: "CONDO", label: "Condo" },
                        { key: "TH", label: "Townhouse" },
                        { key: "VIL", label: "Villa" },
                        { key: "Coach", label: "Coach" },
                      ].map(({ key, label }) => (
                        <div
                          key={key}
                          className="rounded-lg border border-gray-200 p-4 text-center"
                        >
                          <div className="text-2xl font-bold text-gray-900">
                            {homeTypeCountsForCommunity[key] ?? 0}
                          </div>
                          <div className="text-sm text-gray-600">{label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {activeTab === "subdivisions" && (
                <div className="bg-white rounded-lg shadow p-6">
                  <SubdivisionManagement />
                </div>
              )}
              {/* Communities Tab */}
              {activeTab === "communities" && (
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold">Communities</h2>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setCommunityModalOpen(true)}
                    >
                      Add Community
                    </Button>
                  </div>
                  <Table
                    dataSource={communitiesData}
                    columns={communityColumns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                  />
                </div>
              )}

              {/* Users Tab */}
              {activeTab === "users" && (
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold">Users</h2>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setRcrModalOpen(true)}
                    >
                      Add User
                    </Button>
                  </div>
                  <Table
                    dataSource={usersData}
                    columns={userColumns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                  />
                </div>
              )}

              {/* Properties Tab */}
              {activeTab === "properties" && (
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold">Properties</h2>
                  </div>
                  <Table
                    dataSource={propertiesData}
                    columns={propertyColumns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Community Modal */}
        <Modal
          title="Add Community"
          open={communityModalOpen}
          onCancel={() => setCommunityModalOpen(false)}
          footer={null}
          destroyOnClose
        >
          <Form
            layout="vertical"
            form={communityForm}
            onFinish={(values) => createCommunityMutation.mutate(values)}
          >
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Name is required" }]}
            >
              <Input placeholder="Community name" />
            </Form.Item>
            <Form.Item name="shortName" label="Short name">
              <Input placeholder="Short name" />
            </Form.Item>
            <Form.Item name="establishedYear" label="Established year">
              <InputNumber className="w-full" placeholder="e.g., 2001" />
            </Form.Item>
            <Form.Item name="totalHomes" label="Total homes">
              <InputNumber className="w-full" placeholder="e.g., 1000" />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={3} placeholder="Description" />
            </Form.Item>
            <Space className="w-full flex justify-end">
              <Button onClick={() => setCommunityModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createCommunityMutation.isPending}
              >
                Create
              </Button>
            </Space>
          </Form>
        </Modal>

        {/* Add User Modal */}
        <Modal
          title="Add User"
          open={rcrModalOpen}
          onCancel={() => setRcrModalOpen(false)}
          footer={null}
          destroyOnClose
        >
          <Form
            layout="vertical"
            form={rcrForm}
            onFinish={(values) => {
              const roleNorm = selectedRole.toLowerCase().replace(/\s+/g, "");
              const payload = {
                name: values.name,
                email: values.email,
                password: values.password,
                role: selectedRole,
                community_id: parseInt(values.communityId, 10),
              };
              if (roleNorm === "rcrprojectmanager" || roleNorm === "admin") {
                const cid = parseInt(values.communityId, 10);
                const ids = [...managedCommunityIds];
                if (cid > 0 && !ids.includes(cid)) ids.push(cid);
                payload.managedCommunityIds = ids;
              }
              createUserMutation.mutate(payload);
            }}
          >
            <Form.Item
              name="name"
              label="Full name"
              rules={[{ required: true, message: "Name is required" }]}
            >
              <Input placeholder="Full name" />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                {
                  required: true,
                  type: "email",
                  message: "Valid email required",
                },
              ]}
            >
              <Input placeholder="email@example.com" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: "Password is required" },
                { min: 8, message: "Password must be at least 8 characters" },
              ]}
            >
              <Input.Password placeholder="Min 8 characters" />
            </Form.Item>
            <Form.Item label="Admin role" required>
              <select
                className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                {[
                  { label: "Super Admin", value: "SuperAdmin" },
                  { label: "Project Admin", value: "Admin" },
                  { label: "Agent Admin", value: "Agent" },
                ].map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </Form.Item>
            <Form.Item
              name="communityId"
              label="Primary community"
              rules={[{ required: true, message: "Select a community" }]}
            >
              <select
                className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white"
                defaultValue=""
              >
                <option value="" disabled>
                  Select a community
                </option>
                {communitiesData.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.shortName ? `(${c.shortName})` : ""}
                  </option>
                ))}
              </select>
            </Form.Item>
            {["rcrprojectmanager", "admin"].includes(
              selectedRole.toLowerCase().replace(/\s+/g, "")
            ) && (
              <Form.Item label="Managed communities">
                <div className="space-y-1">
                  {communitiesData.map((c) => (
                    <Checkbox
                      key={c.id}
                      checked={managedCommunityIds.includes(c.id)}
                      onChange={(e) => {
                        setManagedCommunityIds((prev) =>
                          e.target.checked
                            ? [...prev, c.id]
                            : prev.filter((id) => id !== c.id)
                        );
                      }}
                    >
                      {c.name} {c.shortName ? `(${c.shortName})` : ""}
                    </Checkbox>
                  ))}
                </div>
              </Form.Item>
            )}
            <Space className="w-full flex justify-end">
              <Button onClick={() => setRcrModalOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createUserMutation.isPending}
              >
                Create
              </Button>
            </Space>
          </Form>
        </Modal>

        {/* Property Detail Modal */}
        <Modal
          title="Property Details"
          open={propertyModalOpen}
          onCancel={() => setPropertyModalOpen(false)}
          footer={null}
          width={800}
          destroyOnClose
        >
          {selectedProperty && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Property #
                  </label>
                  <p className="text-gray-900">
                    {selectedProperty.mls_number}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Community
                  </label>
                  <p className="text-gray-900">
                    {selectedProperty.community?.name}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Home type
                  </label>
                  <p className="text-gray-900">
                    {selectedProperty.home_type || "-"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <p className="text-gray-900">
                    <Tag>{selectedProperty.status || "Unknown"}</Tag>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Listing status
                  </label>
                  <p className="text-gray-900">
                    {selectedProperty.cw_listing_status || "-"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Bedrooms
                  </label>
                  <p className="text-gray-900">
                    {selectedProperty.bedrooms || "-"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Bathrooms
                  </label>
                  <p className="text-gray-900">
                    {selectedProperty.bathrooms || "-"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Price/rent
                  </label>
                  <p className="text-gray-900">
                    {selectedProperty.price_or_rent
                      ? `$${Number(
                          selectedProperty.price_or_rent
                        ).toLocaleString()}`
                      : "-"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Address
                  </label>
                  <p className="text-gray-900">
                    {selectedProperty.street_name &&
                    selectedProperty.city &&
                    selectedProperty.state
                      ? `${selectedProperty.street_name}, ${selectedProperty.city}, ${selectedProperty.state} ${selectedProperty.zip_code}`
                      : "-"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Created
                  </label>
                  <p className="text-gray-900">
                    {selectedProperty.createdAt
                      ? new Date(
                          selectedProperty.createdAt
                        ).toLocaleDateString()
                      : "-"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </main>
    </div>
  );
};

export default SuperAdminDashboard;
